import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { logAudit } from '@/lib/audit';
import {
  Camera,
  Loader2,
  Trash2,
  Eye,
  EyeOff,
  GripVertical,
  Upload,
  AlertTriangle,
} from 'lucide-react';

interface CommunityPhoto {
  id: string;
  image_path: string;
  name: string;
  country: string;
  caption: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

export function CommunityPhotosPage() {
  const [photos, setPhotos] = useState<CommunityPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Upload form state
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Actions in progress
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  // Drag and drop reorder state
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    const { data, error: fetchError } = await supabase
      .from('community_photos')
      .select('*')
      .order('display_order', { ascending: true });

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setPhotos(data ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  function getPublicUrl(path: string) {
    return supabase.storage.from('community').getPublicUrl(path).data.publicUrl;
  }

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !name.trim() || !country.trim()) return;

    setUploading(true);
    setError(null);

    // Upload image to storage
    const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
    const filePath = `${crypto.randomUUID()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('community')
      .upload(filePath, file, { contentType: file.type });

    if (uploadError) {
      setError(uploadError.message);
      setUploading(false);
      return;
    }

    // Determine next display_order
    const maxOrder = photos.reduce((max, p) => Math.max(max, p.display_order), -1);

    // Insert database row
    const { error: insertError } = await supabase.from('community_photos').insert({
      image_path: filePath,
      name: name.trim(),
      country: country.trim(),
      caption: caption.trim(),
      display_order: maxOrder + 1,
    });

    if (insertError) {
      setError(insertError.message);
      setUploading(false);
      return;
    }

    await logAudit({
      action: 'upload_community_photo',
      resourceType: 'community_photo',
      resourceId: filePath,
      metadata: { name: name.trim(), country: country.trim() },
    });

    // Reset form and refresh
    setName('');
    setCountry('');
    setCaption('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploading(false);
    fetchPhotos();
  }

  async function handleToggleActive(photo: CommunityPhoto) {
    setTogglingIds((prev) => new Set(prev).add(photo.id));

    const { error: updateError } = await supabase
      .from('community_photos')
      .update({ is_active: !photo.is_active })
      .eq('id', photo.id);

    if (updateError) {
      setError(updateError.message);
    } else {
      setPhotos((prev) =>
        prev.map((p) => (p.id === photo.id ? { ...p, is_active: !p.is_active } : p)),
      );
      await logAudit({
        action: photo.is_active ? 'deactivate_community_photo' : 'activate_community_photo',
        resourceType: 'community_photo',
        resourceId: photo.id,
      });
    }

    setTogglingIds((prev) => {
      const next = new Set(prev);
      next.delete(photo.id);
      return next;
    });
  }

  async function handleDelete(photo: CommunityPhoto) {
    if (!confirm(`Delete photo of ${photo.name}? This cannot be undone.`)) return;

    setDeletingIds((prev) => new Set(prev).add(photo.id));

    // Delete from storage first
    await supabase.storage.from('community').remove([photo.image_path]);

    // Delete database row
    const { error: deleteError } = await supabase
      .from('community_photos')
      .delete()
      .eq('id', photo.id);

    if (deleteError) {
      setError(deleteError.message);
    } else {
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      await logAudit({
        action: 'delete_community_photo',
        resourceType: 'community_photo',
        resourceId: photo.id,
      });
    }

    setDeletingIds((prev) => {
      const next = new Set(prev);
      next.delete(photo.id);
      return next;
    });
  }

  function handleDragStart(index: number) {
    dragItem.current = index;
  }

  function handleDragEnter(index: number) {
    dragOverItem.current = index;
  }

  async function handleDragEnd() {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) return;

    const reordered = [...photos];
    const [removed] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, removed);

    dragItem.current = null;
    dragOverItem.current = null;

    // Update display_order for all items
    const updated = reordered.map((p, i) => ({ ...p, display_order: i }));
    setPhotos(updated);

    // Persist new order to database
    for (const photo of updated) {
      await supabase
        .from('community_photos')
        .update({ display_order: photo.display_order })
        .eq('id', photo.id);
    }
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 size={24} className="animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Camera size={24} className="text-brand" />
        <h1 className="font-heading text-2xl text-text-primary">Community Photos</h1>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-600" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Upload form */}
      <section className="mb-8 rounded-lg border border-border bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-text-primary">Add Photo</h2>
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-text-primary">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sophie"
                required
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-text-primary">Country *</label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Netherlands"
                required
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-text-primary">Caption</label>
              <input
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Studying Spanish with my morning coffee"
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>

          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium text-text-primary">Image *</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                required
                className="w-full text-sm text-text-muted file:mr-3 file:rounded-lg file:border-0 file:bg-brand-light file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand hover:file:bg-brand/10"
              />
            </div>
            <button
              type="submit"
              disabled={uploading || !file || !name.trim() || !country.trim()}
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </section>

      {/* Photo grid */}
      <section className="rounded-lg border border-border bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-text-primary">Photos ({photos.length})</h2>

        {photos.length === 0 ? (
          <p className="py-8 text-center text-sm text-text-muted">
            No community photos yet. Upload one above to get started.
          </p>
        ) : (
          <div className="space-y-3">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                className={`flex items-center gap-4 rounded-lg border border-border p-3 transition-colors hover:bg-gray-50 ${
                  !photo.is_active ? 'opacity-50' : ''
                }`}
              >
                {/* Drag handle */}
                <div className="cursor-grab text-text-muted active:cursor-grabbing">
                  <GripVertical size={18} />
                </div>

                {/* Thumbnail */}
                <img
                  src={getPublicUrl(photo.image_path)}
                  alt={photo.name}
                  className="h-16 w-16 shrink-0 rounded-lg object-cover"
                />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-text-primary">{photo.name}</p>
                  <p className="text-sm text-text-muted">{photo.country}</p>
                  {photo.caption && (
                    <p className="text-xs text-text-muted truncate">{photo.caption}</p>
                  )}
                </div>

                {/* Status badge */}
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    photo.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {photo.is_active ? 'Active' : 'Hidden'}
                </span>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleActive(photo)}
                    disabled={togglingIds.has(photo.id)}
                    className="rounded-lg p-2 text-text-muted transition-colors hover:bg-gray-100 hover:text-text-primary disabled:opacity-50"
                    title={photo.is_active ? 'Hide' : 'Show'}
                  >
                    {togglingIds.has(photo.id) ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : photo.is_active ? (
                      <EyeOff size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(photo)}
                    disabled={deletingIds.has(photo.id)}
                    className="rounded-lg p-2 text-text-muted transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    title="Delete"
                  >
                    {deletingIds.has(photo.id) ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
